import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtNum, fmtMoney } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Scale, PlayCircle, AlertTriangle, CheckCircle2 } from "lucide-react";

function Stat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-head text-2xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function ReconciliationEngine() {
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => api.get("/reconciliation/summary").then(({ data }) => setSummary(data)).catch(() => setSummary({ rows: [] })), []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reconciliation/auto-run");
      toast.success(`Reconciled ${data.count} agreements · ${data.variances} variance(s) · ${data.exceptions_raised} exception(s) raised`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Scale className="h-6 w-6 text-[#F47920]" /> Automated Reconciliation Engine</h1>
          <p className="text-sm text-slate-500 mt-0.5">Purchased vs Transferred vs Delivered quantity, and invoiced vs paid financial reconciliation with tolerance-based variance detection.</p>
        </div>
        <Button className="bg-[#12346B] hover:bg-[#12346B]/90" onClick={run} disabled={running} data-testid="btn-run-recon">
          {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />} Run Auto-Reconciliation
        </Button>
      </div>

      {!summary ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <Stat label="Agreements Reconciled" value={fmtNum(summary.total)} />
            <Stat label="Balanced" value={fmtNum(summary.balanced)} tone="text-emerald-600" />
            <Stat label="With Variance" value={fmtNum(summary.variances)} tone="text-rose-600" />
            <Stat label="Total Financial Variance" value={fmtMoney(summary.total_financial_variance)} tone="text-[#F47920]" />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="pf-table">
                <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["SPA", "Product", "Purchased", "Transferred", "Delivered", "Qty Var", "Invoiced", "Paid", "Fin. Var", "Result"].map((h) =>
                    <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {(summary.rows || []).length === 0 ? <TableRow><TableCell colSpan={10} className="py-10 text-center text-slate-400 text-sm">No reconciliations yet. Click “Run Auto-Reconciliation”.</TableCell></TableRow>
                    : summary.rows.map((r) => (
                      <TableRow key={r.spa_id} data-testid={`recon-row-${r.spa_id}`} className="border-slate-100">
                        <TableCell className="py-2 px-3 mono text-xs font-semibold">{r.spa_no}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px]">{r.product_name}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(r.purchased)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(r.transferred)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(r.delivered)}</TableCell>
                        <TableCell className={`py-2 px-3 text-[13px] tabular ${Math.abs(r.procurement_variance) > 0 ? "text-orange-600" : ""}`}>{fmtNum(r.procurement_variance)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtMoney(r.invoiced_amount)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtMoney(r.paid_amount)}</TableCell>
                        <TableCell className={`py-2 px-3 text-[13px] tabular ${Math.abs(r.financial_variance) > 0 ? "text-rose-600 font-semibold" : ""}`}>{fmtMoney(r.financial_variance)}</TableCell>
                        <TableCell className="py-2 px-3">
                          {r.recon_status === "Variance"
                            ? <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Variance</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Balanced</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
